import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaAuthors,
  apaInTextAuthors,
  apaName,
  type CitationParts,
  chicagoBibliographyAuthors,
  chicagoNoteAuthors,
  chicagoShortAuthors,
  mlaAuthors,
  mlaInTextAuthors,
  organizationName,
  type PersonName,
  tidyParts,
  withPeriod,
} from "@/lib/tool-kit/citation-parts";
import {
  quotedTitle,
  shortNoteTitle,
  titleCase,
} from "@/lib/tool-kit/citation-sources-more/common";
import {
  capitalizeSubtitle,
  closer,
  englishOrdinal,
  get,
  italic,
  mlaPageRange,
  mlaUrl,
  plain,
  wholeNumber,
} from "@/lib/tool-kit/legal-sources/common";
import {
  beforeSubtitle,
  fullUrl,
  type MediaLabels,
  type MediaValues,
  mlaShortTitle,
  parsePeople,
} from "@/lib/tool-kit/media-sources/common";
import {
  chicagoPageRange,
  publisherName,
  splitPages,
} from "@/lib/tool-kit/special-sources/common";

/*
 * A course lab manual or handout — published by a press, or written by a department
 * and never published — in APA 7, MLA 9, Chicago 18 and ACS.
 *
 * Sources checked (October 2026):
 * - APA 7: no lab-manual example. A published manual is a book (Publication Manual
 *   §10.2): "Beran, J. A., & Lassiter, M. (2022). Laboratory manual for principles of
 *   general chemistry (11th ed.). Wiley." An unpublished one takes a bracketed
 *   description after the italic title; when the department is the author the
 *   university alone is the publisher. A manual on a course site names the site, and
 *   the login page, as in APA's classroom-slide example ("… [PowerPoint slides].
 *   Canvas@FNU. https://fnu.onelogin.com/login"). The in-text citation takes a part:
 *   "(Merenda, 2018, Chapter 14)", so "(Beran & Lassiter, 2022, Experiment 3)".
 * - MLA 9: the MLA Style Center, "How do I cite course materials?" — begin with the
 *   author only if the author is known (do not assume the instructor wrote it),
 *   stand-alone handouts take italic titles, and an unknown author means listing by
 *   title. A department that is also the publisher is not repeated as author.
 * - Chicago 18: a published manual is a book (§14.30 drops the place of publication);
 *   an unpublished one follows §14.114 — title in quotation marks, "unpublished …" and
 *   the date in parentheses in a note, an indication of format at the end, and usually
 *   no bibliography entry if nobody else can obtain it.
 * - ACS: the ACS Guide to Scholarly Communication (2020), §4.3.5 book template —
 *   "Berg, J. M.; Gatto, G. J.; …; Stryer, L. Biochemistry, 10th ed.; Macmillan
 *   Learning, 2023." Authors "Surname, I. I." separated by semicolons, italic title,
 *   edition, then publisher and year; no place of publication for major publishers.
 *   ACS has no lab-manual example, and a department's own manual is the same
 *   template with the university as publisher — an application of the book pattern,
 *   not a quoted example. In-text citations are numbers, so none are produced.
 */

export const LAB_MANUAL_FIELDS = {
  authors: "authors",
  organization: "organization",
  title: "title",
  edition: "edition",
  year: "year",
  publisher: "publisher",
  department: "department",
  university: "university",
  docType: "docType",
  site: "site",
  url: "url",
  format: "format",
  pages: "pages",
  experiment: "experiment",
  shortTitle: "shortTitle",
} as const;

const F = LAB_MANUAL_FIELDS;

/** A manual a press published, or a department's own handout. */
export type LabManualKind = "published" | "departmental";

export interface LabManualLabels extends MediaLabels {
  acs: string;
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
  acsNote: string;
}

interface Manual {
  kind: LabManualKind;
  authors: PersonName[];
  title: string;
  edition: string;
  year: string;
  publisher: string;
  department: string;
  university: string;
  /** "manual" or "handout". */
  docType: string;
  site: string;
  url: string;
  format: string;
  pages: string;
  experiment: string;
  shortTitle: string;
}

const RE_ED_TAIL = /(?:^|\s+)(?:ed\.?|edition)$/i;
const RE_EN_DASH = /–/g;
const RE_ORDINAL_TAIL = /(?:st|nd|rd|th)$/i;

/** "11" → "11th ed."; "Revised" stays as typed, with ed. added if it is missing. */
export function editionLabel(text: string): string {
  const trimmed = text.trim();
  if (trimmed === "") {
    return "";
  }
  const bare = trimmed.replace(RE_ED_TAIL, "").replace(RE_ORDINAL_TAIL, "");
  const number = wholeNumber(bare);
  if (number !== null) {
    return `${englishOrdinal(number)} ed.`;
  }
  return RE_ED_TAIL.test(trimmed) ? trimmed : `${trimmed} ed.`;
}

function readAuthors(values: MediaValues): PersonName[] {
  const text = get(values, F.authors);
  if (get(values, F.organization) === "true") {
    const first = text.split("\n").find((line) => line.trim() !== "");
    return first ? [organizationName(first)] : [];
  }
  return parsePeople(text);
}

function readManual(kind: LabManualKind, values: MediaValues): Manual {
  const departmental = kind === "departmental";
  return {
    kind,
    authors: readAuthors(values),
    title: capitalizeSubtitle(get(values, F.title)),
    edition: editionLabel(get(values, F.edition)),
    year: get(values, F.year),
    publisher: departmental ? "" : get(values, F.publisher),
    department: departmental ? get(values, F.department) : "",
    university: departmental ? get(values, F.university) : "",
    docType: get(values, F.docType) === "handout" ? "handout" : "manual",
    site: departmental ? get(values, F.site) : "",
    url: departmental ? fullUrl(get(values, F.url)) : "",
    format: departmental ? get(values, F.format) : "",
    pages: get(values, F.pages),
    experiment: get(values, F.experiment),
    shortTitle: get(values, F.shortTitle),
  };
}

/** With no personal author, a department stands in as the author (APA, ACS). */
function effectiveAuthors(manual: Manual): PersonName[] {
  if (manual.authors.length > 0 || !manual.department) {
    return manual.authors;
  }
  return [organizationName(manual.department)];
}

/** True when the department is the author and so is not repeated as publisher. */
function departmentIsAuthor(manual: Manual): boolean {
  return manual.authors.length === 0 && manual.department !== "";
}

/* ---------------------------------------------------------------------- APA */

function apaPages(text: string): string {
  const [from, to] = splitPages(text);
  if (from === "") {
    return "";
  }
  return to ? `pp. ${from}–${to}` : `p. ${from}`;
}

function apaDescription(manual: Manual): string {
  if (manual.kind === "published") {
    return "";
  }
  const noun = manual.docType === "handout" ? "lab handout" : "lab manual";
  const label = manual.site ? noun : `unpublished ${noun}`;
  return ` [${label.charAt(0).toUpperCase()}${label.slice(1)}]`;
}

function apaSource(manual: Manual): string {
  if (manual.kind === "published") {
    return publisherName(manual.publisher, "apa");
  }
  if (manual.site) {
    return manual.site;
  }
  if (departmentIsAuthor(manual)) {
    return manual.university;
  }
  return [manual.department, manual.university].filter(Boolean).join(", ");
}

function apaReference(manual: Manual): CitationParts {
  const authors = effectiveAuthors(manual);
  const year = manual.year || "n.d.";
  const edition = manual.edition ? ` (${manual.edition})` : "";
  const source = apaSource(manual);
  const tail = [source ? withPeriod(source) : "", manual.url]
    .filter(Boolean)
    .join(" ");
  const description = `${edition}${apaDescription(manual)}.`;
  const tailText = tail ? ` ${tail}` : "";
  if (authors.length === 0) {
    return tidyParts([
      italic(manual.title),
      plain(`${description} (${year}).${tailText}`),
    ]);
  }
  const names = apaAuthors(authors);
  return tidyParts([
    plain(`${names}${closer(names)} (${year}). `),
    italic(manual.title),
    plain(`${description}${tailText}`),
  ]);
}

function apaLocator(manual: Manual): string {
  const parts = [manual.experiment, apaPages(manual.pages)].filter(Boolean);
  return parts.length > 0 ? `, ${parts.join(", ")}` : "";
}

function apaOutput(manual: Manual, labels: LabManualLabels): CitationOutput {
  const authors = effectiveAuthors(manual);
  const year = manual.year || "n.d.";
  const locator = apaLocator(manual);
  const short = beforeSubtitle(titleCase(manual.title));
  const has = authors.length > 0;
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: apaReference(manual) },
      {
        label: labels.parenthetical,
        parts: has
          ? [plain(`(${apaInTextAuthors(authors)}, ${year}${locator})`)]
          : [plain("("), italic(short), plain(`, ${year}${locator})`)],
      },
      {
        label: labels.narrative,
        parts: has
          ? [plain(`${apaInTextAuthors(authors, true)} (${year}${locator})`)]
          : [italic(short), plain(` (${year}${locator})`)],
      },
    ],
    note: labels.apaNote,
  };
}

/* ---------------------------------------------------------------------- MLA */

function mlaPage(text: string): string {
  const [from, to] = splitPages(text);
  return mlaPageRange(from, to).replace(RE_EN_DASH, "-");
}

function mlaWorksCited(manual: Manual): CitationParts {
  const title = titleCase(manual.title);
  const parts: CitationParts = [];
  if (manual.authors.length > 0) {
    const names = mlaAuthors(manual.authors);
    parts.push(plain(`${names}${closer(names)} `));
  }
  parts.push(italic(title), plain(`${closer(title)} `));
  if (manual.site) {
    const rest = [manual.year, manual.url ? mlaUrl(manual.url) : ""]
      .filter(Boolean)
      .join(", ");
    parts.push(italic(manual.site), plain(rest ? `, ${rest}.` : "."));
    return tidyParts(parts);
  }
  const publisher =
    manual.kind === "published"
      ? publisherName(manual.publisher, "mla")
      : [manual.department, manual.university].filter(Boolean).join(", ");
  const elements = [manual.edition, publisher, manual.year].filter(Boolean);
  parts.push(plain(withPeriod(elements.join(", "))));
  return tidyParts(parts);
}

function mlaOutput(manual: Manual, labels: LabManualLabels): CitationOutput {
  const page = mlaPage(manual.pages);
  const stamp = page ? ` ${page}` : "";
  const parts: CitationParts =
    manual.authors.length > 0
      ? [plain(`(${mlaInTextAuthors(manual.authors)}${stamp})`)]
      : [
          plain("("),
          italic(mlaShortTitle(titleCase(manual.title))),
          plain(`${stamp})`),
        ];
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: mlaWorksCited(manual) },
      { label: labels.inText, parts },
    ],
    note: labels.mlaNote,
  };
}

/* ------------------------------------------------------------------ Chicago */

function chicagoPage(text: string): string {
  const [from, to] = splitPages(text);
  return chicagoPageRange(from, to);
}

function chicagoUnpublishedNoteDetails(manual: Manual): string {
  const noun = manual.docType === "handout" ? "lab handout" : "lab manual";
  const holder = manual.site
    ? [manual.site]
    : [manual.department, manual.university];
  return [`unpublished ${noun}`, ...holder, manual.year]
    .filter(Boolean)
    .join(", ");
}

function chicagoNote(manual: Manual, title: string): CitationParts {
  const lead =
    manual.authors.length > 0 ? `${chicagoNoteAuthors(manual.authors)}, ` : "";
  const page = chicagoPage(manual.pages);
  if (manual.kind === "published") {
    const imprint = [publisherName(manual.publisher, "chicago"), manual.year]
      .filter(Boolean)
      .join(", ");
    const edition = manual.edition ? `, ${manual.edition}` : "";
    const tail = `${imprint ? ` (${imprint})` : ""}${page ? `, ${page}` : ""}.`;
    return tidyParts([plain(lead), italic(title), plain(`${edition}${tail}`)]);
  }
  const format = manual.format ? `, ${manual.format}` : "";
  const url = manual.url ? `, ${manual.url}` : "";
  const loc = page ? `, ${page}` : "";
  return [
    plain(
      `${lead}${quotedTitle(title, "")} (${chicagoUnpublishedNoteDetails(manual)})${loc}${format}${url}.`
    ),
  ];
}

function chicagoShortNote(manual: Manual, title: string): CitationParts {
  const short = manual.shortTitle || shortNoteTitle(title);
  const page = chicagoPage(manual.pages);
  const lead =
    manual.authors.length > 0 ? `${chicagoShortAuthors(manual.authors)}, ` : "";
  if (manual.kind === "published") {
    return tidyParts([
      plain(lead),
      italic(short),
      plain(page ? `, ${page}.` : "."),
    ]);
  }
  const quoted = page
    ? `${quotedTitle(short, ",")} ${page}.`
    : quotedTitle(short, ".");
  return [plain(`${lead}${quoted}`)];
}

function chicagoBibliography(manual: Manual, title: string): CitationParts {
  const names =
    manual.authors.length > 0 ? chicagoBibliographyAuthors(manual.authors) : "";
  const head = names ? `${names}${closer(names)} ` : "";
  if (manual.kind === "published") {
    const publisher = publisherName(manual.publisher, "chicago");
    const imprint = [publisher, manual.year].filter(Boolean).join(", ");
    const edition = manual.edition ? ` ${manual.edition}` : "";
    return tidyParts([
      plain(head),
      italic(title),
      plain(
        `${closer(title)}${edition}${imprint ? ` ${withPeriod(imprint)}` : ""}`
      ),
    ]);
  }
  const details = chicagoUnpublishedNoteDetails(manual);
  const format = manual.format ? ` ${withPeriod(manual.format)}` : "";
  const url = manual.url ? ` ${manual.url}.` : "";
  return [
    plain(
      `${head}${quotedTitle(title, ".")} ${withPeriod(`${details.charAt(0).toUpperCase()}${details.slice(1)}`)}${format}${url}`
    ),
  ];
}

function chicagoOutput(
  manual: Manual,
  labels: LabManualLabels
): CitationOutput {
  const title = titleCase(manual.title);
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      { label: labels.footnote, parts: chicagoNote(manual, title) },
      { label: labels.shortNote, parts: chicagoShortNote(manual, title) },
      { label: labels.bibliography, parts: chicagoBibliography(manual, title) },
    ],
    note: labels.chicagoNote,
  };
}

/* ---------------------------------------------------------------------- ACS */

function acsAuthors(names: PersonName[]): string {
  return names.map(apaName).join("; ");
}

function acsReference(manual: Manual): CitationParts {
  const authors = effectiveAuthors(manual);
  const title = titleCase(manual.title);
  const names = authors.length > 0 ? acsAuthors(authors) : "";
  const publisher =
    manual.kind === "published"
      ? publisherName(manual.publisher, "apa")
      : manual.site ||
        (departmentIsAuthor(manual)
          ? manual.university
          : [manual.department, manual.university].filter(Boolean).join(", "));
  const edition = manual.edition ? `, ${manual.edition}` : "";
  const imprint = [publisher, manual.year].filter(Boolean).join(", ");
  const url = manual.url ? ` ${manual.url}` : "";
  return tidyParts([
    plain(names ? `${names}${closer(names)} ` : ""),
    italic(title),
    plain(`${edition}${imprint ? `; ${imprint}` : ""}.${url}`),
  ]);
}

function acsOutput(manual: Manual, labels: LabManualLabels): CitationOutput {
  return {
    key: "acs",
    label: labels.acs,
    entries: [{ label: labels.referenceList, parts: acsReference(manual) }],
    note: labels.acsNote,
  };
}

export function labManualIsReady(values: MediaValues): boolean {
  return get(values, F.title) !== "";
}

export function formatLabManual(
  kind: LabManualKind,
  values: MediaValues,
  labels: LabManualLabels
): CitationOutput[] {
  const manual = readManual(kind, values);
  return [
    apaOutput(manual, labels),
    mlaOutput(manual, labels),
    chicagoOutput(manual, labels),
    acsOutput(manual, labels),
  ];
}
